import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import type { UseStockInput } from '@jupi/shared'
import type { FleetAircraft } from './queries'

// Take units out of a lot for a job: how many, into which aircraft, a note.
export function UseStockDialog({
  open,
  max,
  unit,
  aircraft,
  defaultRegistration = '',
  saving,
  onUse,
  onClose,
}: {
  open: boolean
  max: number
  unit: string
  aircraft: FleetAircraft[]
  // Pre-selected aircraft, when the dialog is opened from an aircraft's page.
  defaultRegistration?: string
  saving: boolean
  onUse: (use: UseStockInput) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [quantity, setQuantity] = useState('1')
  const [registration, setRegistration] = useState('')
  const [note, setNote] = useState('')
  useEffect(() => {
    if (open) {
      setQuantity('1')
      setRegistration(defaultRegistration)
      setNote('')
    }
  }, [open, defaultRegistration])

  const units = Number(quantity)
  const valid = Number.isInteger(units) && units >= 1 && units <= max

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('inventory.useTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={`${t('inventory.useQuantity')} (${unit})`}
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            error={quantity !== '' && !valid}
            helperText={t('inventory.useMax', { max })}
            slotProps={{ htmlInput: { min: 1, max, step: 1, inputMode: 'numeric' } }}
            autoFocus
          />
          <TextField select label={t('inventory.useAircraft')} value={registration} onChange={(e) => setRegistration(e.target.value)}>
            <MenuItem value="">{t('inventory.useNoAircraft')}</MenuItem>
            {aircraft.map((a) => (
              <MenuItem key={a.registration} value={a.registration}>
                {a.registration}
              </MenuItem>
            ))}
          </TextField>
          <TextField label={t('inventory.useNote')} value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || saving}
          onClick={() => onUse({ quantity: units, registration: registration || null, note: note.trim() })}
        >
          {t('inventory.use')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
